// src/lib/learning/levelScoring.js

export const XP_PER_SCORE_POINT = 10;

/**
 * Legacy browser scores are estimates, NOT proof of earned XP.
 * Only a trusted backend can verify redesigned Knowledge Check awards.
 * Do not use this switch to enable secure scoring by itself.
 */
export function isVerifiedXpCutoverEnabled() {
  return (
    process.env.REACT_APP_SECURE_KC_ENABLED === "true" &&
    process.env.REACT_APP_VERIFIED_XP_CUTOVER_ENABLED === "true"
  );
}


export const LEVEL_SCORE_CONFIG = {
  L1:  { levelKey: "L1",  kScoreMax: 3, aScoreMax: 0, hasActionPage: false },
  L2:  { levelKey: "L2",  kScoreMax: 3, aScoreMax: 1, hasActionPage: true },
  L3:  { levelKey: "L3",  kScoreMax: 3, aScoreMax: 1, hasActionPage: true },
  L4:  { levelKey: "L4",  kScoreMax: 3, aScoreMax: 0, hasActionPage: false },
  L5:  { levelKey: "L5",  kScoreMax: 3, aScoreMax: 1, hasActionPage: true },
  L6:  { levelKey: "L6",  kScoreMax: 3, aScoreMax: 1, hasActionPage: true },
  L7:  { levelKey: "L7",  kScoreMax: 2, aScoreMax: 1, hasActionPage: true },
  L8:  { levelKey: "L8",  kScoreMax: 3, aScoreMax: 0, hasActionPage: false },
  L9:  { levelKey: "L9",  kScoreMax: 3, aScoreMax: 0, hasActionPage: false },
  L10: { levelKey: "L10", kScoreMax: 2, aScoreMax: 1, hasActionPage: true },
  L11: { levelKey: "L11", kScoreMax: 3, aScoreMax: 0, hasActionPage: false },
};

export function normalizeLevelKey(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return "";
  }

  const directMatch = raw.match(/^L(\d+)$/i);

  if (directMatch?.[1]) {
    return `L${Number(directMatch[1])}`;
  }

  const numberMatch = raw.match(/(\d+)/);

  if (numberMatch?.[1]) {
    return `L${Number(numberMatch[1])}`;
  }

  return raw.toUpperCase();
}

export function clampScore(value, max) {
  const parsed = Number(value || 0);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(parsed, max));
}

export function scoreToXp(scoreValue) {
  const parsed = Number(scoreValue || 0);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, parsed) * XP_PER_SCORE_POINT;
}

export function getLevelScoreConfig(levelKey) {
  const normalizedLevelKey = normalizeLevelKey(levelKey);
  const config = LEVEL_SCORE_CONFIG[normalizedLevelKey];

  if (config) {
    const totalScoreMax = config.kScoreMax + config.aScoreMax;

    return {
      ...config,
      totalScoreMax,
      kXpMax: scoreToXp(config.kScoreMax),
      aXpMax: scoreToXp(config.aScoreMax),
      totalXpMax: scoreToXp(totalScoreMax),
    };
  }

  /*
   * Safe future default:
   * future levels can still have K score,
   * but no A score until we add them here.
   */
  return {
    levelKey: normalizedLevelKey,
    kScoreMax: 3,
    aScoreMax: 0,
    hasActionPage: false,
    totalScoreMax: 3,
    kXpMax: 30,
    aXpMax: 0,
    totalXpMax: 30,
  };
}

export function shouldShowActionPageForLevel(levelKey) {
  return getLevelScoreConfig(levelKey).hasActionPage === true;
}

export function getDefaultActionTaskKey(levelKey) {
  const config = getLevelScoreConfig(levelKey);
  return `${config.levelKey.toLowerCase()}-application-task`;
}

export function getLevelScoreStorageKey(levelKey) {
  return `gwin_level_score_${normalizeLevelKey(levelKey)}`;
}

export function getKnowledgeScoreStoragePrefix(levelKey) {
  return `gwin_k_score_${normalizeLevelKey(levelKey)}_`;
}

export function getKnowledgeScoreStorageKey({ levelKey, questionKey } = {}) {
  const safeQuestionKey = String(questionKey || "knowledge-check")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `${getKnowledgeScoreStoragePrefix(levelKey)}${safeQuestionKey}`;
}

export function saveKnowledgeCheckCompletion({
  levelKey,
  questionKey,
  scoreValue = 1,
} = {}) {
  if (typeof window === "undefined") {
    return null;
  }

  const config = getLevelScoreConfig(levelKey);

  const key = getKnowledgeScoreStorageKey({
    levelKey: config.levelKey,
    questionKey,
  });

  try {
    const existing = window.sessionStorage.getItem(key);

    if (existing) {
      return JSON.parse(existing);
    }

    const kScoreEarned = clampScore(scoreValue, 1);

    const payload = {
      levelKey: config.levelKey,
      questionKey: questionKey || "knowledge-check",
      kScoreEarned,
      kXpEarned: scoreToXp(kScoreEarned),
      completedAt: new Date().toISOString(),
    };

    window.sessionStorage.setItem(key, JSON.stringify(payload));

    return payload;
  } catch {
    return null;
  }
}

export function readKnowledgeScoreSummary(levelKey) {
  const config = getLevelScoreConfig(levelKey);

  if (typeof window === "undefined") {
    return {
      levelKey: config.levelKey,
      kScoreEarned: 0,
      kScoreMax: config.kScoreMax,
      kXpEarned: 0,
      kXpMax: config.kXpMax,
    };
  }

  try {
    const prefix = getKnowledgeScoreStoragePrefix(config.levelKey);
    let kScoreEarned = 0;

    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);

      if (!key || !key.startsWith(prefix)) {
        continue;
      }

      const raw = window.sessionStorage.getItem(key);

      if (!raw) {
        continue;
      }

      const payload = JSON.parse(raw);
      kScoreEarned += Number(payload?.kScoreEarned || 0);
    }

    const finalKScore = clampScore(kScoreEarned, config.kScoreMax);

    return {
      levelKey: config.levelKey,
      kScoreEarned: finalKScore,
      kScoreMax: config.kScoreMax,
      kXpEarned: scoreToXp(finalKScore),
      kXpMax: config.kXpMax,
    };
  } catch {
    return {
      levelKey: config.levelKey,
      kScoreEarned: 0,
      kScoreMax: config.kScoreMax,
      kXpEarned: 0,
      kXpMax: config.kXpMax,
    };
  }
}

export function getActionStorageKey({ levelKey, taskKey } = {}) {
  const normalizedLevelKey = normalizeLevelKey(levelKey);

  const safeTaskKey = String(
    taskKey || getDefaultActionTaskKey(normalizedLevelKey)
  )
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `gwin_action_score_${normalizedLevelKey}_${safeTaskKey}`;
}

export function isActionOptionScoreEligible(option) {
  if (!option) {
    return false;
  }

  const explicitEligible =
    option.aScoreEligible ??
    option.isAEligible ??
    option.scoreEligible ??
    option.eligible;

  if (explicitEligible === false) {
    return false;
  }

  const explicitText = String(explicitEligible || "")
    .trim()
    .toLowerCase();

  if (explicitText === "false" || explicitText === "no") {
    return false;
  }

  const value = String(
    option.value ||
      option.id ||
      option.backendValue ||
      option.title ||
      option.label ||
      ""
  )
    .trim()
    .toLowerCase();

  if (
    value === "not_sure" ||
    value === "not-sure" ||
    value === "unsure" ||
    value.includes("not sure")
  ) {
    return false;
  }

  return true;
}

export function buildActionCompletionPayload({
  levelKey,
  taskKey,
  taskMode,
  selectedOption,
  answers = {},
} = {}) {
  const config = getLevelScoreConfig(levelKey);

  const resolvedTaskKey =
    taskKey || getDefaultActionTaskKey(config.levelKey);

  const actionAllowed = config.hasActionPage && config.aScoreMax > 0;
  const optionEligible = isActionOptionScoreEligible(selectedOption);

  const aScoreEarned = actionAllowed && optionEligible ? config.aScoreMax : 0;

  const applicationState =
    aScoreEarned > 0 ? "completed" : "pending_support";

  return {
    levelKey: config.levelKey,
    taskKey: resolvedTaskKey,
    taskMode: taskMode || "application_task",

    selectedOptionId:
      selectedOption?.id ||
      selectedOption?.value ||
      selectedOption?.backendValue ||
      null,

    selectedOptionTitle:
      selectedOption?.title ||
      selectedOption?.label ||
      selectedOption?.text ||
      selectedOption?.optionText ||
      "",

    answers,

    kScoreEarned: 0,
    aScoreEarned,
    aXpEarned: scoreToXp(aScoreEarned),
    xpEarned: scoreToXp(aScoreEarned),

    applicationState,
    actionAllowed,
    optionEligible,

    completedAt: new Date().toISOString(),
  };
}

export function saveActionCompletion(payload) {
  if (typeof window === "undefined" || !payload) {
    return payload;
  }

  try {
    const key = getActionStorageKey({
      levelKey: payload.levelKey,
      taskKey: payload.taskKey,
    });

    window.sessionStorage.setItem(key, JSON.stringify(payload));

    return payload;
  } catch {
    return payload;
  }
}

export function readActionCompletion({ levelKey, taskKey } = {}) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const key = getActionStorageKey({
      levelKey,
      taskKey: taskKey || getDefaultActionTaskKey(levelKey),
    });

    const raw = window.sessionStorage.getItem(key);

    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function buildLevelCompletionScore({
  levelKey,
  taskKey,
} = {}) {
  const config = getLevelScoreConfig(levelKey);

  const knowledgeScore = readKnowledgeScoreSummary(config.levelKey);

  const actionCompletion = readActionCompletion({
    levelKey: config.levelKey,
    taskKey: taskKey || getDefaultActionTaskKey(config.levelKey),
  });

  const kScoreEarned = clampScore(
    knowledgeScore.kScoreEarned,
    config.kScoreMax
  );

  const aScoreEarned = clampScore(
    actionCompletion?.aScoreEarned || 0,
    config.aScoreMax
  );

  const totalScoreEarned = kScoreEarned + aScoreEarned;

  return {
    levelKey: config.levelKey,

    kScoreEarned,
    kScoreMax: config.kScoreMax,
    kXpEarned: scoreToXp(kScoreEarned),
    kXpMax: config.kXpMax,

    aScoreEarned,
    aScoreMax: config.aScoreMax,
    aXpEarned: scoreToXp(aScoreEarned),
    aXpMax: config.aXpMax,

    totalScoreEarned,
    totalScoreMax: config.totalScoreMax,
    totalXpEarned: scoreToXp(totalScoreEarned),
    totalXpMax: config.totalXpMax,

    hasActionPage: config.hasActionPage,
    // This number is displayed only in the legacy mode. Never use it
    // as an award amount after switching to verified scoring.
    isBrowserEstimate: true,
  };
}